/**
 * Enter page (design: index.html): "enter" -> email ("send me a code") + Continue with Google -> 6-digit code.
 * Same login order as today's LoginScreen/VerifyCodeScreen (src/legacy/components/auth/):
 *   invite link (?invite=&email=) -> check the invite, keep its token;
 *   else existing profile / pending invite (check-by-email) / anyone else -> request-otp (unknown emails become applicants);
 *   verify-otp -> member: Home (onboarding runs there) | applicant: Request to join.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { SUPPORT_EMAIL } from '@/config/brandConfig'
import { API_URL, IS_MOCK } from '~/config/mode'
import { enterSprite } from '~/design/sprites'
import { Sprite } from '~/app/ui'
import { Falling } from './Falling'

type Step = 'enter' | 'email' | 'code'
const I = ({ id }: { id: string }) => <svg className="icon" aria-hidden="true"><use href={`#${id}`} /></svg>

export function Enter() {
  const navigate = useNavigate()
  const { requestOTP, login, loginWithGoogle, error, clearError } = useAuth()
  const params = new URLSearchParams(location.search)
  const inviteToken = params.get('invite')
  const [step, setStep] = useState<Step>(inviteToken ? 'email' : 'enter')
  const [email, setEmail] = useState(params.get('email') ? decodeURIComponent(params.get('email')!) : '')
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''))
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState('')
  const boxes = useRef<(HTMLInputElement | null)[]>([])
  const googleBox = useRef<HTMLDivElement>(null)
  const GOOGLE = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined

  const say = (m: string) => { setToast(m); setTimeout(() => setToast(''), 2600) }
  const afterLogin = (r: { isApplicant?: boolean }) => {
    if (r.isApplicant) return navigate('/apply')
    const back = params.get('returnTo')
    navigate(back === '/vip' || back === '/status' ? back : '/home', { replace: true })
  }

  // Google: same as today (button drawn by Google; only on addresses allowed in Caryn's Google client)
  const onGoogle = useCallback(async (res: { credential: string }) => {
    setBusy(true); setErr(''); clearError()
    try { afterLogin(await loginWithGoogle(res.credential)) } catch (e) { setErr(e instanceof Error ? e.message : 'Google sign-in failed') } finally { setBusy(false) }
  }, [loginWithGoogle])
  useEffect(() => {
    if (!GOOGLE || step !== 'email') return
    const t = setInterval(() => {
      const g = (window as any).google?.accounts?.id
      if (!g || !googleBox.current) return
      clearInterval(t)
      g.initialize({ client_id: GOOGLE, callback: onGoogle })
      g.renderButton(googleBox.current, { theme: 'outline', size: 'large', text: 'continue_with', shape: 'pill', width: 300 })
    }, 120)
    return () => clearInterval(t)
  }, [GOOGLE, step, onGoogle])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setStep((s) => (s === 'code' ? 'email' : 'enter')) }
    document.addEventListener('keydown', onKey); return () => document.removeEventListener('keydown', onKey)
  }, [])
  useEffect(() => { if (step === 'code') setTimeout(() => boxes.current[0]?.focus(), 50) }, [step])

  async function sendCode(e: FormEvent) {
    e.preventDefault()
    setErr(''); clearError()
    const v = email.trim()
    if (!/^\S+@\S+\.\S+$/.test(v)) { setErr('Please enter a valid email address.'); return }
    setBusy(true)
    try {
      if (inviteToken) {
        const r = await fetch(`${API_URL}/api/subscriptions/invites/${encodeURIComponent(inviteToken)}`)
        if (!r.ok) { setErr('This invite link is invalid or has expired. Please contact support.'); return }
        const { invite } = await r.json()
        if (invite.email.toLowerCase() !== v.toLowerCase()) { setErr('Please use the email address this invite was sent to.'); return }
        if (invite.status !== 'pending') { setErr('This invite has already been claimed or has expired.'); return }
        localStorage.setItem('pending_invite_token', inviteToken)
      } else {
        try {
          const p = await fetch(`${API_URL}/api/auth/check-profile?email=${encodeURIComponent(v)}`)
          const hasProfile = p.ok && (await p.json()).hasProfile
          if (!hasProfile) {
            const i = await fetch(`${API_URL}/api/subscriptions/invites/check-by-email?email=${encodeURIComponent(v)}`)
            if (i.ok) { const d = await i.json(); if (d.hasInvite && d.invite?.token) localStorage.setItem('pending_invite_token', d.invite.token) }
          }
        } catch { /* same as today: these checks never block sending the code */ }
      }
      await requestOTP(v)
      setDigits(Array(6).fill(''))
      setStep('code')
    } catch { /* message shown from the auth context */ } finally { setBusy(false) }
  }

  async function verify(e?: FormEvent) {
    e?.preventDefault()
    setErr(''); clearError()
    const code = digits.join('')
    if (code.length !== 6) { setErr('Please enter all 6 digits.'); return }
    setBusy(true)
    try { afterLogin(await login(email.trim(), code)) } catch { /* shown below */ } finally { setBusy(false) }
  }
  const setDigit = (i: number, v: string) => {
    const d = v.replace(/\D/g, '').slice(-1)
    setDigits((prev) => { const n = [...prev]; n[i] = d; if (n.every(Boolean) && i === 5) setTimeout(() => (document.getElementById('codeSubmit') as HTMLButtonElement)?.click(), 0); return n })
    if (d && boxes.current[i + 1]) boxes.current[i + 1]!.focus()
  }
  const paste = (e: React.ClipboardEvent) => {
    const d = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6)
    if (!d) return
    e.preventDefault()
    setDigits(Array.from({ length: 6 }, (_, k) => d[k] || ''))
    boxes.current[Math.min(d.length, 5)]?.focus()
  }
  const shownErr = err || error || ''

  return (
    <div className="cw-enter">
      <Sprite svg={enterSprite} />
      <Falling />
      <main className="stage">
        <section className="copy">
          <h1 className="wordmark">caryn’s world <I id="i-heart" /></h1>
          <p className="eyebrow">Invite-only community</p>
          <div className="flourish" aria-hidden="true"><I id="i-heart" /></div>
          <div className="card" id="auth">
            {step === 'enter' && (
              <div className="step">
                <button className="btn" type="button" autoFocus onClick={() => setStep('email')}>enter <I id="i-arrow" /></button>
                <p className="terms">by logging in you agree to the <a href="/terms">terms of service</a> and <a href="/privacy">privacy policy</a></p>
              </div>
            )}
            {step === 'email' && (
              <form className="step" noValidate onSubmit={sendCode}>
                <h2>Welcome in</h2>
                <p className="hint">We’ll email you a 6-digit code. No password needed.</p>
                <label className="field">Your email
                  <input type="email" inputMode="email" autoComplete="email" placeholder="you@example.com" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
                </label>
                <p className="err" role="alert">{shownErr}</p>
                <button className="btn" type="submit" disabled={busy}>{busy ? 'sending…' : 'send me a code'} <I id="i-arrow" /></button>
                {(GOOGLE || IS_MOCK) && <div className="or" aria-hidden="true">or</div>}
                {GOOGLE ? <div ref={googleBox} style={{ display: 'flex', justifyContent: 'center', minHeight: 44 }} />
                  : IS_MOCK ? <button className="btn btn-google" type="button" onClick={() => onGoogle({ credential: 'demo' })}><GoogleG /> continue with Google</button> : null}
                <button className="back" type="button" onClick={() => setStep('enter')}>← back</button>
              </form>
            )}
            {step === 'code' && (
              <form className="step" noValidate onSubmit={verify}>
                <h2>Check your inbox</h2>
                <p className="hint">Enter the 6-digit code we sent to <strong>{email}</strong>.</p>
                <div className="otp" role="group" aria-label="6-digit code" onPaste={paste}>
                  {digits.map((d, i) => (
                    <input key={i} ref={(el) => { boxes.current[i] = el }} inputMode="numeric" autoComplete={i === 0 ? 'one-time-code' : 'off'} maxLength={1} aria-label={`Digit ${i + 1}`}
                      value={d} onChange={(e) => setDigit(i, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Backspace' && !digits[i] && i > 0) boxes.current[i - 1]?.focus() }} />
                  ))}
                </div>
                <p className="err" role="alert">{shownErr}</p>
                <button className="btn" type="submit" id="codeSubmit" disabled={busy}>{busy ? 'checking…' : 'verify & enter'} <I id="i-arrow" /></button>
                <div className="row2">
                  <button className="back" type="button" disabled={busy} onClick={async () => { try { await requestOTP(email.trim()); say('A new code is on its way.') } catch { /* shown */ } }}>resend code</button>
                  <button className="back" type="button" onClick={() => setStep('email')}>use a different email</button>
                </div>
                {IS_MOCK && <p className="terms">Demo: no email is sent; any 6 digits work. member@example.com = member · welcome@example.com = new member (first-login steps) · expired@example.com = access ended · any other email = application.</p>}
              </form>
            )}
          </div>
          <a className="support" style={{ textDecoration: 'none' }} href={`mailto:${SUPPORT_EMAIL}`}><I id="i-help" />Support</a>
        </section>
        <div className="pumpkin"><img src="/design/pumpkin.webp" width={640} height={529} alt="A hand-drawn autumn pumpkin with a heart-shaped vine and leaf" /></div>
      </main>
      <div className={`toast${toast ? ' show' : ''}`} role="status" aria-live="polite">{toast}</div>
    </div>
  )
}

export function GoogleG() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.1 2.7-2.4 3.6v3h3.9c2.2-2.1 3.5-5.1 3.5-8.8z" />
      <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1C3.3 21.3 7.4 24 12 24z" />
      <path fill="#FBBC05" d="M5.3 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3V6.6H1.3C.5 8.2 0 10.1 0 12s.5 3.8 1.3 5.4l4-3.1z" />
      <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4C18 1.2 15.2 0 12 0 7.4 0 3.3 2.7 1.3 6.6l4 3.1c.9-2.9 3.6-4.9 6.7-4.9z" />
    </svg>
  )
}
